import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { useSuppressionsList, useClearSuppression } from '@/features/email-suppressions/api';
import { sanitiseEmailForTestId } from '@/features/email-suppressions/sanitise';
import type { EmailSuppressionRow } from '@honeycomb/shared';

export const Route = createFileRoute('/_authed/suppressions/')({
  component: SuppressionsPage,
});

function reasonTone(reason: EmailSuppressionRow['reason']): 'honey' | 'neutral' | 'sage' {
  if (reason === 'hard_bounce') return 'honey';
  if (reason === 'complaint') return 'honey';
  return 'neutral';
}

function ClearConfirmDialog({
  email,
  onConfirm,
  onClose,
  isPending,
  errorMessage,
}: {
  email: string;
  onConfirm: () => void;
  onClose: () => void;
  isPending: boolean;
  errorMessage?: string;
}) {
  return (
    <Dialog open onClose={onClose} title="Clear suppression" testId="suppression-clear-dialog">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">
          Remove the active suppression for <strong>{email}</strong>? The next send to this address
          will no longer be blocked.
        </p>
        {errorMessage && (
          <p className="text-sm text-red-600" data-testid="suppression-clear-error">
            {errorMessage}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isPending}
            data-testid="suppression-clear-confirm"
          >
            {isPending ? 'Clearing…' : 'Clear suppression'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function SuppressionsPage() {
  const { data, isLoading, isError, error } = useSuppressionsList({ activeOnly: true });
  const clearMutation = useClearSuppression();
  const [pendingClear, setPendingClear] = useState<string | null>(null);
  const [clearError, setClearError] = useState<string | null>(null);

  const suppressions = data?.suppressions ?? [];

  const handleClearConfirm = () => {
    if (!pendingClear) return;
    setClearError(null);
    clearMutation.mutate(pendingClear, {
      onSuccess: () => {
        setPendingClear(null);
        setClearError(null);
      },
      onError: (err) => {
        setClearError(err instanceof Error ? err.message : 'Failed to clear suppression. Please try again.');
      },
    });
  };

  return (
    <div
      className="p-4 sm:p-6 lg:p-8"
      data-testid="suppressions-page"
    >
      <header className="mb-6">
        <h1 className="font-serif text-3xl leading-tight">Suppressions</h1>
        {!isLoading && (
          <p className="text-ink-soft text-sm mt-1">
            {suppressions.length} active suppression{suppressions.length === 1 ? '' : 's'}
          </p>
        )}
      </header>

      <Card shadow="sm" className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-ink-soft">Loading…</div>
        ) : isError ? (
          <div className="p-6 text-ink-soft">
            Failed to load suppressions{error instanceof Error ? `: ${error.message}` : '.'}
          </div>
        ) : suppressions.length === 0 ? (
          <div className="p-6 text-ink-faint" data-testid="suppressions-empty">
            No active suppressions.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                style={{ borderColor: 'var(--color-hairline)' }}
              >
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Failures</th>
                <th className="px-4 py-3">First failed</th>
                <th className="px-4 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {suppressions.map((row) => {
                const sanitised = sanitiseEmailForTestId(row.email);
                return (
                  <tr
                    key={row.email}
                    className="border-b last:border-b-0"
                    style={{ borderColor: 'var(--color-hairline)' }}
                    data-testid={`suppression-row-${sanitised}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs break-all">{row.email}</td>
                    <td className="px-4 py-3">
                      <Badge
                        tone={reasonTone(row.reason)}
                        data-testid={`suppression-row-reason-${sanitised}`}
                      >
                        {row.reason}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs tabular-nums">
                      {row.failureCount}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {new Date(row.firstFailedAt).toISOString().slice(0, 10)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`suppression-row-clear-${sanitised}`}
                        onClick={() => setPendingClear(row.email)}
                        disabled={clearMutation.isPending && pendingClear === row.email}
                      >
                        Clear
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {pendingClear !== null && (
        <ClearConfirmDialog
          email={pendingClear}
          onConfirm={handleClearConfirm}
          onClose={() => { setPendingClear(null); setClearError(null); }}
          isPending={clearMutation.isPending}
          errorMessage={clearError ?? undefined}
        />
      )}
    </div>
  );
}
